import { Tooltip, Form, Input, Checkbox, message } from '@nackle/paper';
import { QuestionCircle } from '@nackle/icons/dist/react-es/antd-filled';
import { navigate } from '@reach/router';
import { ARCADE_PATH_CREATE } from 'app/components/routes/paths';
import { danger } from 'app/components/shared/helper-text-helpers';
import { getArcade, setArcadeValue } from 'arcade/sections/arcade/actions';
import unset from 'lodash/fp/unset';
import isEmpty from 'lodash/isEmpty';
import React, { Component, FormEvent } from 'react';
import { HasPermission } from 'shared/HasPermission';
import { ValidationMapOf } from 'shared/helper-text-helpers/types';
import { Room } from 'shared/interfaces';
import SaveButton from 'shared/save-button';
import { AnyFormElement } from 'utils/viewtypes';
import { Arcade } from './interfaces';
interface ArcadeFormProps {
    readonly arcade: Arcade;
    readonly path: string;
    readonly arcadeId: string;
    readonly setArcadeValue: typeof setArcadeValue;
    readonly createArcade: (arcade: Arcade) => Promise<any>;
    readonly updateArcade: (arcade: Arcade, arcadeId: string) => Promise<any>;
    readonly getArcade: typeof getArcade;
    readonly roomOptions: Array<Room>;
}

interface State {
    isSaving: boolean;
    validationErrors: ValidationMapOf<Arcade>;
}

class ArcadeForm extends Component<ArcadeFormProps, State> {
    static defaultProps: Partial<ArcadeFormProps> = {
        roomOptions: []
    };

    state: State = {
        isSaving: false,
        validationErrors: {}
    };

    constructor(props: ArcadeFormProps) {
        super(props);
        if (!this.isCreateMode()) {
            props.getArcade(props.arcadeId);
        }
    }

    componentDidUpdate(prevProps: ArcadeFormProps) {
        const { arcade } = this.props;
        if (this.isCreateMode() && !prevProps.arcadeId && !!arcade.id) {
            navigate(`./${arcade.id}/update`);
        }
    }

    handleValue = (event: FormEvent<AnyFormElement>) => {
        const { name, value } = event.target as AnyFormElement;
        if (!(name === 'name' || name === 'mainRoomId')) {
            return;
        }
        this.props.setArcadeValue(name, value);
        this.clearError(name);
    };

    handleCheckboxValue = (event: any) => {
        const { name, checked } = event.target as HTMLInputElement;
        if (!(name === 'autoplayPreferenceEnabled' || name === 'hideInstantPlays' || name === 'hideArcadeFloorLink')) {
            return;
        }
        this.props.setArcadeValue(name, checked);
        this.clearError(name);
    };

    handleSave = async () => {
        const { arcade, arcadeId } = this.props;
        const validationErrors = this.validateAll(this.props.arcade);
        if (!isEmpty(validationErrors)) {
            this.setState({ validationErrors });
            message.error({
                content: 'Unable to save Arcade: One or more fields is invalid. Please check the form and try again.',
            });
            return Promise.reject();
        }

        try {
            return (this.isCreateMode()) ?
                await this.props.createArcade(arcade) :
                await this.props.updateArcade(arcade, arcadeId);
        } catch (e) {
            console.error(e);
            message.error({
                content: 'Unable to save Arcade: A server error occurred.',
            });
        }
    };

    validateAll = (arcade: Arcade) => {
        const ret: ValidationMapOf<Arcade> = {};
        if (isEmpty(arcade.name)) {
            ret.name = danger('This field is required');
        }
        return ret;
    };

    private clearError(fieldName: keyof Arcade) {
        this.setState(unset(['validationErrors', fieldName]));
    }

    render() {
        let mainRoomId = this.props.arcade.mainRoomId;
        const {
            arcade: {
                name
            },
            roomOptions
        } = this.props;
        const { validationErrors } = this.state;
        mainRoomId = mainRoomId || '' ;

        return (
            <div className="program">
            <Form layout="vertical">
                <Form.Item
                    label="Arcade Name"
                    {...validationErrors['name']}
                >
                    <Input
                        placeholder="Arcade Name"
                        name="name"
                        {...validationErrors['name']}
                        value={ name }
                        type="text"
                        onChange={this.handleValue}
                    />
                        <br></br>
                        <br></br>
                </Form.Item>
                <Form.Item
                    label="Main Room"
                    help={this.isCreateMode() &&
                        'A main room will be automatically created when the arcade is saved.'
                    }
                ></Form.Item>
                    <select
                        id="mainRoomId"
                        name="mainRoomId"
                        value={mainRoomId}
                        onChange={this.handleValue}
                        disabled={!mainRoomId}
                        style= {{ width: 300 }}
                    >
                        <option value="">Select</option>
                        {
                            roomOptions.map(option =>
                                <option
                                    key={`arcade-main-room-option-${option.id}`}
                                    value={option.id}>
                                    {option.name}
                                </option>
                            )
                        }
                    </select>
                </Form>
                        <br></br>
                <Form>
                    <Checkbox
                        checked={this.props.arcade.autoplayPreferenceEnabled !== false}
                        onChange={this.handleCheckboxValue}
                        name="autoplayPreferenceEnabled"
                    >
                        Enable Auto-Play option for users
                        {' '}
                        <Tooltip
                            className={'tooltip-indictor bp3-text-muted bp3-text-small'}
                            title={<>
                                Auto-Play lets users receive Token-based rewards without playing games in the Arcade.
                                <br />
                                This shows a switch that lets users turn Auto-Play on or off.
                            </>}
                        >
                             <QuestionCircle/>
                        </Tooltip>
                    </Checkbox>
                    <br></br>
                    <Checkbox
                        checked={Boolean(this.props.arcade.hideInstantPlays)}
                        onChange={this.handleCheckboxValue}
                        name="hideInstantPlays"
                    >
                        Hide Instant Plays
                        {' '}
                        <Tooltip
                            className={'tooltip-indicator bp3-text-muted bp3-text-small'}
                            title={<>
                                Instant Play games can be played directly from the Arcade Widget.
                                <br />
                                This hides the &quot;Instant Plays&quot; section in the Widget<br />
                                if they have no instant plays available.
                            </>}
                        >
                            <QuestionCircle/> 
                        </Tooltip>
                    </Checkbox>
                    <br></br>
                    <Checkbox
                        checked={Boolean(this.props.arcade.hideArcadeFloorLink)}
                        onChange={this.handleCheckboxValue}
                        name="hideArcadeFloorLink"
                    >
                        Hide the &quot;Go Play In the Arcade&quot; section
                        {' '}
                        <Tooltip
                            className={'tooltip-indicator bp3-text-muted bp3-text-small'}
                            title={<>
                                This hides the &quot;Arcade&quot; section in the Widget,
                                which includes the token balance and a button to launch the Arcade FLoor
                            </>}
                        >
                            <QuestionCircle/>
                        </Tooltip>
                    </Checkbox>
                </Form>
                <br></br>
                <Form>
                    <HasPermission app="arcade-api" permission="edit-arcade">
                        {(hasPermission: boolean) =>
                            <SaveButton
                                disabled={!isEmpty(validationErrors) || !hasPermission}
                                save={this.handleSave}
                            />
                        }
                    </HasPermission>
                </Form>
                <br></br>
            </div>
        );
    }

    private isCreateMode() {
        return this.props.path === ARCADE_PATH_CREATE;
    }
}

export default ArcadeForm;

